import type {
  NarrativeFact,
  NarrativeMemoryPayload,
  NarrativeMemoryRetrieval,
} from "./types";
import type { NarrativeIngest } from "./schemas";
const normalize = (value: string) =>
  value
    .toLowerCase()
    .replace(/[\s，。！？、；：,.!?;:'"“”‘’（）()\[\]【】]/g, "");
const tokens = (value: string) =>
  new Set(
    value
      .toLowerCase()
      .split(/[\s，。！？、；：,.!?;:'"“”‘’（）()\[\]【】/]+/)
      .filter((token) => token.length > 1),
  );
export function mergeNarrativeFacts(
  previous: NarrativeFact[],
  ingest: NarrativeIngest,
  turn: number,
): NarrativeFact[] {
  const next = [...previous];
  for (const incoming of ingest.facts) {
    const key = `${incoming.kind}:${normalize(incoming.subject)}:${normalize(incoming.content)}`;
    const exact = next.find(
      (fact) =>
        `${fact.kind}:${normalize(fact.subject)}:${normalize(fact.content)}` ===
        key,
    );
    if (exact) {
      exact.lastSeenTurn = turn;
      exact.confidence = incoming.confidence;
      exact.keywords = [
        ...new Set([...exact.keywords, ...incoming.keywords]),
      ].slice(0, 12);
      continue;
    }
    const sameSubject = next.find(
      (fact) =>
        fact.kind === incoming.kind &&
        normalize(fact.subject) === normalize(incoming.subject) &&
        similarity(fact.content, incoming.content) > 0.72,
    );
    if (sameSubject) {
      sameSubject.content = incoming.content;
      sameSubject.lastSeenTurn = turn;
      sameSubject.confidence = incoming.confidence;
      sameSubject.keywords = incoming.keywords;
      continue;
    }
    next.push({
      id: `NM_${turn}_${next.length + 1}`,
      firstSeenTurn: turn,
      lastSeenTurn: turn,
      ...incoming,
    });
  }
  return next.sort((a, b) => b.lastSeenTurn - a.lastSeenTurn).slice(0, 240);
}
function similarity(a: string, b: string) {
  const aa = tokens(a),
    bb = tokens(b);
  if (!aa.size && !bb.size) return 1;
  let common = 0;
  for (const token of aa) if (bb.has(token)) common++;
  return common / Math.max(aa.size, bb.size);
}
export function retrieveNarrativeMemory(
  memory: NarrativeMemoryPayload,
  query: string,
  limit = 12,
): NarrativeMemoryRetrieval {
  const queryTokens = tokens(query);
  const facts = [...memory.facts]
    .map((fact) => {
      let score = Math.min(2, fact.lastSeenTurn / 1000);
      for (const token of tokens(
        `${fact.subject} ${fact.content} ${fact.keywords.join(" ")}`,
      ))
        if (queryTokens.has(token)) score += 3;
      return { fact, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((item) => item.fact);
  const rendered = [
    memory.summary && `摘要：${memory.summary}`,
    ...facts.map(
      (fact) => `- [${fact.confidence}] ${fact.subject}：${fact.content}`,
    ),
  ]
    .filter(Boolean)
    .join("\n");
  return { summary: memory.summary, facts, rendered };
}
